import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import { evaluateParameter, evaluateSpec } from "./calcEngine"

// QC results / test execution (F-C2, SPEC-005 SP-C; QC lifecycle reworked in SPEC-011;
// SPEC-021 cutover: persisted via the REAL /lims/results endpoints). When an analyst enters QC
// results for an analysis, the values are judged against that analysis definition's F-B1
// specification. The SERVER is authoritative (SP-E Phase 3.3): it freezes/refreshes the spec
// snapshot from the live server-side spec while the record is a draft (LOCKED at send-to-review),
// RECOMPUTES every judged field (value/pass/computedValue/overall) from the client's RAW inputs,
// stamps enteredBy/enteredAt, drives the lifecycle state machine and writes every audit entry.
// The FE's own judgement (judgeValue/judgeCalculatedParameter/computeOverall below) is kept as
// LIVE feedback while typing — the persisted record always carries the server's recomputation.
//
// QC lifecycle (SPEC-011 + SPEC-012, enforced server-side):
//   draft -> in-review -> sent-to-qa -> (per sample) qa-approved | qa-rerun | qa-stopped
//   - The analyst enters values and clicks "Send to review" (NO e-sig) -> in-review. The server
//     enforces the two send-to-review HARD GATES (expired/discarded reference standard; not-passed
//     SST) with HTTP 422; a reviewer gate-override e-signature rides on the SAME call (password +
//     reason top-level) — see sendToReview(override).
//   - The reviewer may set okOverride (e-SIGNED when it flips a computed FAIL) or mark OOT
//     (not signed), then "Send to QA" (reviewer e-signature, 4-eyes ≠ enterer) -> sent-to-qa.
//   - "Return for correction" sends the record back to `draft` with a mandatory comment.
//   - QA decides per sample via setQaDecision (qa capability, 4-eyes ≠ enterer/reviewer).
//
// E-signature transport (SPEC-021 B1): every signed transition sends the signer's `password`
// (+ optional `reason`) at the TOP LEVEL of the request body. The backend's sign() verifies the
// password, enforces capability + 4-eyes, writes the immutable signature + audit entry and
// discards the password. The FE never persists the password and never embeds it in the patch.
//
// Record shape (server dump):
//   {
//     id, analysisId, analysisDefinitionId, sampleId,
//     specSnapshot: { parameters: [...], formula },     // server-frozen copy of the applied spec
//     values: [{ parameterId, name, unit, criterionType, min, max, value, pass,
//                // SPEC-016 — only on a CALCULATED parameter's value:
//                inputs, formula, computedValue }],
//     reagentLotIds: [...], instrumentId,
//     overall: "PASS" | "FAIL" | null,
//     state: "draft" | "in-review" | "sent-to-qa" | "qa-approved" | "qa-rerun" | "qa-stopped",
//     okOverride, okOverrideSignature,                   // server-stamped signature object
//     oot, ootComment,
//     reviewerSignature, qaSignature,                    // server-stamped signature objects
//     reviewComment, reviewedBy, reviewedAt, enteredBy, enteredAt
//   }
const initialState = { items: [], isLoading: false }

// The QC result lifecycle states (single source of truth for the string values, so they are not
// duplicated across the reducers/selectors/helpers below).
const STATE = {
  DRAFT: "draft",
  IN_REVIEW: "in-review",
  // SPEC-035 (F3): the supervisor (chemist) review step, inserted between in-review and sent-to-qa
  // ONLY when the analysis definition's review spec sets requiresSupervisorReview (F0). When unset
  // the flow is the unchanged in-review → sent-to-qa reviewer sign-off.
  SENT_TO_SUPERVISOR: "sent-to-supervisor",
  SENT_TO_QA: "sent-to-qa",
  QA_APPROVED: "qa-approved",
  QA_RERUN: "qa-rerun",
  QA_STOPPED: "qa-stopped",
}

// SPEC-012 per-sample QA decisions, committed by the single "QA Reviewed" e-signature. Each maps
// 1:1 onto the terminal result state the decision drives the record to.
export const QA_DECISION = { APPROVE: STATE.QA_APPROVED, RERUN: STATE.QA_RERUN, STOP: STATE.QA_STOPPED }

function sortByEnteredDesc(items) {
  return [...items].sort((a, b) => ((a.enteredAt || "") < (b.enteredAt || "") ? 1 : -1))
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsResults/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsResults/fetch":
      return { items: sortByEnteredDesc(action.res || []), isLoading: false }
    case "limsResults/saveDraft": {
      const item = action.res
      const existing = state.items.some((x) => x.id === item.id)
      const items = existing
        ? state.items.map((x) => (x.id === item.id ? item : x))
        : [...state.items, item]
      return { ...state, items: sortByEnteredDesc(items) }
    }
    // In-place record updates: replace the matching item with the server's returned record. SPEC-035
    // (F3): "limsResults/sendToSupervisor" MUST be listed here — it is the reviewer's send-to-supervisor
    // (its own action type). Without it the action fell through to `default`, so after a successful
    // PATCH the store kept the result at "in-review" and the supervisor phase never populated (the
    // hand-off appeared to do nothing).
    case "limsResults/sendToReview":
    case "limsResults/sendToQa":
    case "limsResults/sendToSupervisor":
    case "limsResults/setOverride":
    case "limsResults/setOot":
    case "limsResults/return":
    case "limsResults/setQaDecision":
      return {
        ...state,
        items: state.items.map((x) => (action.res && x.id === action.res.id ? action.res : x)),
      }
    // amendQaDecision returns the FULL response envelope ({ item, autoHold, existingHold }) so the
    // caller can surface the hold side effects — the reducer updates the record from action.res.item.
    case "limsResults/amendQaDecision": {
      const item = action.res && action.res.item
      if (!item) return state
      return {
        ...state,
        items: state.items.map((x) => (x.id === item.id ? item : x)),
      }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsResults/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/results").then((res) => res.items)
})

// Human-readable label for a result row — the analysis name (resolved from the planned/unplanned
// analysis, else the snapshotted definition), never the result/sample UUID.
function resultLabel(state, record) {
  const analyses = state.analyses || {}
  const analysis =
    (analyses.plannedAnalyses && analyses.plannedAnalyses[record.analysisId]) ||
    (analyses.unplannedAnalyses && analyses.unplannedAnalyses[record.analysisId])
  if (analysis && analysis.name) return analysis.name
  const def = record.analysisDefinitionId && (state.analysisDefinitions || {})[record.analysisDefinitionId]
  if (def && def.name) return def.name
  return record.analysisDefinitionId || record.analysisId || record.id || ""
}

// Split the ESignatureModal payload into the top-level signing fields the backend reads
// ({ password, reason }) — the password is NEVER embedded in a patch or persisted anywhere.
function signingFields(signature) {
  if (!signature) return {}
  const fields = {}
  if (signature.password) fields.password = signature.password
  if (signature.reason) fields.reason = signature.reason
  return fields
}

// Upsert a result as a draft (the analyst's working copy). The caller has built the RAW
// `values` (measured value / calculated inputs per parameter); the SERVER recomputes the
// judged fields, freezes the spec snapshot and stamps enteredBy/enteredAt + the audit entry.
// Returns the saved (server-recomputed) record.
export const saveDraft = asyncAction("limsResults/saveDraft", (_dispatch, record) => {
  return limsRequest("POST", "/lims/results", { input: record }).then((res) => res.item)
})

// Hand a saved draft on to review (F-C3): flip its state to "in-review". The server enforces
// the two send-to-review hard gates (reference standard / SST) with HTTP 422; `override` is the
// reviewer gate-override e-signature payload — its password + reason ride on the SAME call, so
// the server signs the deviation and transitions atomically (SPEC-021 gate-override flow).
export const sendToReview = asyncAction("limsResults/sendToReview", (_dispatch, id, override = null) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { state: STATE.IN_REVIEW },
    ...signingFields(override),
  }).then((res) => res.item)
})

// Second-person review SEND-TO-QA (SPEC-011): a qualified reviewer (≠ the analyst, holds the
// "reviewer" capability — enforced server-side, mirrored fail-fast by the e-signature dialog)
// signs off an in-review result and hands it to QA. The server verifies the password, stamps
// reviewerSignature/reviewedBy/reviewedAt and writes the audit entry.
export const sendToQa = asyncAction("limsResults/sendToQa", (_dispatch, id, signature) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { state: STATE.SENT_TO_QA },
    ...signingFields(signature),
  }).then((res) => res.item)
})

// SPEC-035 (F3): the reviewer signs an in-review result on to the SUPERVISOR (chemist) when the
// analysis definition requires a supervisor review (the F0 requiresSupervisorReview flag). Same
// reviewer e-signature gate as sendToQa (reviewer capability, 4-eyes ≠ enterer — enforced
// server-side); only the target state differs. The server 409s when the definition does not
// require it (then sendToQa is the right call).
export const sendToSupervisor = asyncAction("limsResults/sendToSupervisor", (_dispatch, id, signature) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { state: STATE.SENT_TO_SUPERVISOR },
    ...signingFields(signature),
  }).then((res) => res.item)
})

// SPEC-035 (F3): the SUPERVISOR e-signs a sent-to-supervisor result on to QA (supervisor
// capability, 4-eyes ≠ enterer + reviewer — enforced server-side). The supervisor also has FULL
// review power in their phase (setOverride / setOot / open OOS), reusing the existing thunks; this
// is the approve-to-QA step.
export const supervisorApprove = asyncAction("limsResults/sendToQa", (_dispatch, id, signature) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { state: STATE.SENT_TO_QA },
    ...signingFields(signature),
  }).then((res) => res.item)
})

// SPEC-035 (F3): the SUPERVISOR returns a sent-to-supervisor result to the TECHNICAL reviewer
// (→ "in-review") with a MANDATORY comment. Not e-signed (review-level feedback; the reviewer owns
// the correction loop with the analyst). The server clears the supervisor sign-off and records the
// comment.
export const supervisorReturnForReview = asyncAction("limsResults/return", (_dispatch, id, comment) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { state: STATE.IN_REVIEW, supervisorComment: comment || "" },
  }).then((res) => res.item)
})

// Reviewer-only per-sample OK OVERRIDE (SPEC-011): the reviewer forces an effective Pass on a
// result whose computed Judgement is Fail. The RAW computed `overall`/`values` stay untouched —
// the server stamps okOverride + its signature. An override that flips a computed FAIL requires
// the e-signature password (server-enforced); clearing does not.
export const setOverride = asyncAction("limsResults/setOverride", (_dispatch, id, override, signature = null) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { okOverride: !!override },
    ...signingFields(signature),
  }).then((res) => res.item)
})

// Reviewer-only per-sample OUT-OF-TREND flag (SPEC-011): in-spec but atypical, plus an optional
// comment. Not e-signed (informational). Clearing OOT also clears the comment (server-side).
export const setOot = asyncAction("limsResults/setOot", (_dispatch, id, oot, comment = null) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { oot: !!oot, ootComment: oot ? comment || "" : null },
  }).then((res) => res.item)
})

// Second-person review RETURN (F-C3): the reviewer sends an in-review result back to the
// analyst for correction. The server flips state to "draft" and records the MANDATORY
// reviewer comment. Not e-signed.
export const returnForCorrection = asyncAction("limsResults/return", (_dispatch, id, comment) => {
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { state: STATE.DRAFT, reviewComment: comment || "" },
  }).then((res) => res.item)
})

// SPEC-012 QA per-sample decision: commit one of qa-approved / qa-rerun / qa-stopped onto a
// `sent-to-qa` result. The server verifies the QA password (qa capability, 4-eyes ≠ enterer +
// reviewer), stamps qaSignature and writes the audit entry. The RERUN-creation (qa-rerun) and
// batch-reject (qa-stopped) side effects are orchestrated by the caller (the QA Analysis Review
// modal — B-D5: result-QA stays state-only; reruns/holds are FE-created). Returns the record.
export const setQaDecision = asyncAction("limsResults/setQaDecision", (_dispatch, id, decision, signature = null) => {
  const next =
    decision === QA_DECISION.RERUN || decision === QA_DECISION.STOP ? decision : QA_DECISION.APPROVE
  return limsRequest("PATCH", `/lims/results/${id}`, {
    patch: { state: next },
    ...signingFields(signature),
  }).then((res) => res.item)
})

// PRD-006 K13 (a) — AMEND a QA-terminal decision: a QA typo can be corrected as a NEW signed,
// reason-mandatory action that SUPERSEDES the existing decision. The original signature + audit are
// preserved; the server appends a `decisionHistory` entry and verifies the QA password (qa
// capability, 4-eyes ≠ enterer + reviewer) exactly as the first decision did. `nextState` is the
// corrected terminal state (qa-approved | qa-rerun | qa-stopped, ≠ the current one); the signature's
// reason is MANDATORY (server 422s on a blank reason — the ESignatureModal flow mirrors this).
//
// Unlike setQaDecision this returns the FULL response envelope, not just the record, because the
// server reports the conditional batch-hold side effects under the SAME signature:
//   - `autoHold` ({ created:true, batchLot } | { created:false, reason:"no-batch-lot" }) when
//     amending TO qa-stopped — the lot is held automatically (or nothing is, surfaced).
//   - `existingHold` ({ batchLot }) when amending AWAY FROM qa-stopped while an active hold remains
//     — the hold is NOT auto-lifted; the caller warns it must be lifted separately if intended.
// The caller (QaDecisionSection) surfaces both via the message system. The record carries the new
// state + the appended `decisionHistory` for the chain display.
export const amendQaDecision = asyncAction(
  "limsResults/amendQaDecision",
  (_dispatch, id, nextState, signature = null) => {
    const next =
      nextState === QA_DECISION.RERUN || nextState === QA_DECISION.STOP ? nextState : QA_DECISION.APPROVE
    return limsRequest("PATCH", `/lims/results/${id}`, {
      patch: { state: next, amend: true },
      ...signingFields(signature),
    })
  }
)

// ---- Judgement helpers (LIVE feedback while typing; the server recomputes on save) ----

// Locale-robust numeric coercion. Spec bounds and entered values are kept as the raw strings
// the analyst typed (so partial/decimal input is never clobbered mid-edit), and Danish users
// type a decimal COMMA — so normalise "," → "." before Number(). Returns NaN for blank/garbage.
export function toNumber(x) {
  if (x === "" || x === null || x === undefined) return NaN
  return Number(typeof x === "string" ? x.replace(",", ".") : x)
}

// Auto-judge a single numeric value against one spec parameter's acceptance criterion:
//   "min"   → value ≥ min
//   "max"   → value ≤ max
//   "range" → min ≤ value ≤ max
// Returns null when the value is blank/non-numeric or the parameter has no usable bound, so
// callers can distinguish "not yet judged" from a hard pass/fail.
export function judgeValue(parameter, rawValue) {
  if (!parameter) return null
  if (rawValue === "" || rawValue === null || rawValue === undefined) return null
  const value = toNumber(rawValue)
  if (Number.isNaN(value)) return null
  const { criterionType, min, max } = parameter
  const minN = toNumber(min)
  const maxN = toNumber(max)
  const hasMin = !Number.isNaN(minN)
  const hasMax = !Number.isNaN(maxN)
  switch (criterionType) {
    case "min":
      return hasMin ? value >= minN : null
    case "max":
      return hasMax ? value <= maxN : null
    case "range":
      if (!hasMin || !hasMax) return null
      return value >= minN && value <= maxN
    default:
      return null
  }
}

// SPEC-023: resolve the engine evaluation for one calculated parameter. With a spec `context`
// ({ parameters, entries }) the WHOLE spec is evaluated via evaluateSpec, so an input with
// source "parameter" binds to its sibling parameter's ROUNDED value and chains re-judge live as
// source values are typed. Without a context (or when the parameter is not part of it) the
// legacy single-parameter path runs unchanged — callers without sibling access, and specs
// without references, behave exactly as before. Both paths yield evaluateParameter's shape +
// `resolvedInputs` (the input map actually evaluated — parameter-sourced keys resolved).
function evaluateForJudgement(parameter, inputValues, context) {
  if (context && Array.isArray(context.parameters) && parameter && parameter.id) {
    const result = evaluateSpec(context.parameters, context.entries).byId[parameter.id]
    if (result) return result
  }
  return { ...evaluateParameter(parameter, inputValues || {}), resolvedInputs: inputValues || null }
}

// SPEC-016: judge a CALCULATED parameter. Runs the calculation engine over the analyst's raw
// `inputValues`, judges the engine's ROUNDED value against the parameter's min/max/range criterion
// (reusing judgeValue), and — for an aggregate parameter — additionally requires every individual
// value to be within the ±tolerance limit. `context` (optional, SPEC-023) is { parameters,
// entries }: the full spec + the sample's entries, letting parameter-sourced inputs resolve from
// sibling parameters via evaluateSpec. Returns the full result the panel snapshots:
//   { computedValue, rounded, pass, perValueChecks, error, resolvedInputs }
//   - pass: true | false | null (null = not yet computable / error / no criterion)
// The aggregate pass = the RSD/criterion pass AND every individual value within tolerance.
export function judgeCalculatedParameter(parameter, inputValues, context = null) {
  const evald = evaluateForJudgement(parameter, inputValues, context)
  if (evald.error || evald.computedValue === null) {
    return { computedValue: null, rounded: null, pass: null, perValueChecks: evald.perValueChecks || null, error: evald.error || null, resolvedInputs: evald.resolvedInputs || null }
  }
  // Judge the ROUNDED (reported) value against the criterion — exactly as a measured value.
  let pass = judgeValue(parameter, evald.rounded)
  // Aggregate individual-limit: any out-of-tolerance unit fails the parameter (even if RSD passes).
  if (pass !== null && Array.isArray(evald.perValueChecks) && evald.perValueChecks.length > 0) {
    const allWithin = evald.perValueChecks.every((c) => c.withinTolerance)
    pass = pass && allWithin
  }
  return { computedValue: evald.computedValue, rounded: evald.rounded, pass, perValueChecks: evald.perValueChecks || null, error: null, resolvedInputs: evald.resolvedInputs || null }
}

// Build the RAW value entry sent for one CALCULATED parameter: the entered inputs are what the
// server trusts (it re-runs its calc-engine port over them); the locally judged fields ride along
// only for symmetric shape — the server ignores + recomputes them. With a SPEC-023 `context` the
// snapshotted `inputs` are the RESOLVED map: parameter-sourced keys carry the sibling value the
// engine actually evaluated, so the record reproduces the calculation without re-resolving
// references against state that may have changed since (GMP reproducibility).
export function buildCalculatedValue(parameter, inputValues, context = null) {
  const judged = judgeCalculatedParameter(parameter, inputValues, context)
  return {
    parameterId: parameter.id,
    name: parameter.name,
    unit: parameter.unit,
    criterionType: parameter.criterionType,
    min: parameter.min ?? null,
    max: parameter.max ?? null,
    value: judged.rounded,
    pass: judged.pass,
    inputs: judged.resolvedInputs || inputValues || {},
    formula: parameter.formula || "",
    computedValue: judged.computedValue,
  }
}

// Overall = PASS only when EVERY judged parameter passes; FAIL if any judged parameter fails.
// With nothing judged it is null ("not yet judged") — EXCEPT for a spec that has no parameters at
// all, which is a PASS (R20, #4084).
//
// The parameter-less PASS is opt-in via `parameters`: a caller that does not pass the spec cannot
// tell "nothing entered yet" from "nothing to enter", so omitting the argument keeps the old
// null. Only a caller that KNOWS the spec is empty may claim the verdict. This mirrors the server's
// `results._compute_overall(values, parameters)` (SPEC-056 R20), which is keyed on the frozen
// snapshot's parameters for the same reason: an empty `values` list is not evidence of an empty
// spec.
export function computeOverall(values, parameters = null) {
  const judged = (values || []).filter((v) => v.pass !== null && v.pass !== undefined)
  if (judged.length > 0) return judged.every((v) => v.pass === true) ? "PASS" : "FAIL"
  return parameters && parameters.length === 0 ? "PASS" : null
}

// The EFFECTIVE judgement shown for a saved record: the reviewer's okOverride forces an effective
// PASS, but the RAW computed `overall` is preserved on the record and surfaced separately by the
// UI ("Fail (override)"). null/non-FAIL records are returned unchanged.
export function effectiveOverall(record) {
  if (!record) return null
  if (record.okOverride && record.overall === "FAIL") return "PASS"
  return record.overall ?? null
}

// Whether a result has cleared second-person review (the reviewer signed it on to QA, or QA has
// already approved it). The SPEC-011 release gate kept working through this INTERIM helper until
// A3 reworked the gate to strictly "QA-approved"; consumers like the consumable-lock still read it.
export function isReviewSignedOff(record) {
  const s = record && record.state
  return s === STATE.SENT_TO_QA || s === STATE.QA_APPROVED
}

// Whether a result has been QA-approved (the SPEC-012 terminal, release-eligible disposition).
// The Sample View surfaces a "QA-approved" icon for these.
export function isQaApproved(record) {
  return !!record && record.state === STATE.QA_APPROVED
}

// RR3 #8: whether QA has reached ANY terminal decision on the result (approved / rerun / stopped).
// Once decided, opening a fresh OOS investigation from the result panel is moot, so the action hides.
export function isQaDecided(record) {
  const s = record && record.state
  return s === STATE.QA_APPROVED || s === STATE.QA_RERUN || s === STATE.QA_STOPPED
}

// ---- Selectors ----

// All results entered for one analysis (most recent first). Usually 0 or 1, but the model
// does not forbid re-entry, so this returns a list and the panel takes the latest.
export const selectResultsForAnalysis = (state, analysisId) =>
  state.limsResults.items.filter((r) => r.analysisId === analysisId)

// The single most-recent result for one analysis (or undefined).
export const selectResultForAnalysis = (state, analysisId) =>
  selectResultsForAnalysis(state, analysisId)[0]

// The result for a specific (analysis, sample) pair — the per-sample result-entry row resumes
// from this. Each sample of an analysis is judged individually (same spec, value-or-OK).
export const selectResultForAnalysisSample = (state, analysisId, sampleId) =>
  state.limsResults.items.find((r) => r.analysisId === analysisId && r.sampleId === sampleId)

// Whether a specific sample has a QA-approved result (SPEC-012). The Sample View marks the exact
// sample-analysis with a "QA-approved" icon. A sampleId is unique to one (analysis, observation)
// pairing, so this is a safe per-sample-analysis lookup.
export const selectIsSampleQaApproved = (state, sampleId) =>
  !!sampleId && state.limsResults.items.some((r) => r.sampleId === sampleId && isQaApproved(r))

// SPEC-018: whether a specific sample carries a computed-FAIL result — the precondition for OPENING
// an OOS investigation from the Sample View. The RAW `overall` is used (not the effective override),
// because a reviewer's OK-override does not erase the underlying out-of-spec result that warrants an
// investigation.
export const selectIsSampleFail = (state, sampleId) =>
  !!sampleId && state.limsResults.items.some((r) => r.sampleId === sampleId && r.overall === "FAIL")

// SPEC-027: whether an analysis has ANY QC result yet — the proxy for "the analysis is in progress".
// Used by the unplan GMP-guard: once an analysis has any result, none of its samples may be unplanned
// (removing a sample would drop its QC data). Authoritative enforcement is backend-side.
export const selectAnalysisHasResults = (state, analysisId) =>
  !!analysisId && state.limsResults.items.some((r) => r.analysisId === analysisId)

// Results handed on to review (F-C3 consumes this as its inbox).
export const selectResultsAwaitingReview = (state) =>
  state.limsResults.items.filter((r) => r.state === STATE.IN_REVIEW)

// In-review results for ONE analysis — the review block in the result panel acts on these.
export const selectInReviewResultsForAnalysis = (state, analysisId) =>
  state.limsResults.items.filter((r) => r.analysisId === analysisId && r.state === STATE.IN_REVIEW)

// Set of analysis ids that have ≥1 result awaiting review. The Team Plan "awaiting review"
// indicator reads this so a reviewer can spot them without a separate screen.
export const selectAnalysisIdsAwaitingReview = (state) => {
  const ids = new Set()
  state.limsResults.items.forEach((r) => {
    if (r.state === STATE.IN_REVIEW) ids.add(r.analysisId)
  })
  return ids
}

// SPEC-035 (F3): results awaiting the supervisor (chemist) review — the reviewer signed them on to
// the supervisor (state "sent-to-supervisor"). The supervisor phase in the result panel acts on
// these; a supervisor worklist could consume this too.
export const selectResultsSentToSupervisor = (state) =>
  state.limsResults.items.filter((r) => r.state === STATE.SENT_TO_SUPERVISOR)

// Sent-to-supervisor results for ONE analysis — the supervisor block in the result panel acts on these.
export const selectSentToSupervisorResultsForAnalysis = (state, analysisId) =>
  state.limsResults.items.filter(
    (r) => r.analysisId === analysisId && r.state === STATE.SENT_TO_SUPERVISOR
  )

// Set of analysis ids that have ≥1 result awaiting the supervisor review. A Team Plan "awaiting
// supervisor" indicator can read this so a supervisor can spot them without a separate screen.
export const selectAnalysisIdsAwaitingSupervisor = (state) => {
  const ids = new Set()
  state.limsResults.items.forEach((r) => {
    if (r.state === STATE.SENT_TO_SUPERVISOR) ids.add(r.analysisId)
  })
  return ids
}

// Set of analysis ids whose results have been signed off by the reviewer and handed to QA
// (state "sent-to-qa"). SPEC-012's QA surface reads this to find work awaiting a QA decision.
export const selectAnalysisIdsSentToQa = (state) => {
  const ids = new Set()
  state.limsResults.items.forEach((r) => {
    if (r.state === STATE.SENT_TO_QA) ids.add(r.analysisId)
  })
  return ids
}

// SPEC-012 QA Analysis Review worklist: one entry per analysis that has ≥1 result STILL awaiting a
// QA decision (state "sent-to-qa"). Each entry carries the analysis label (resolved like the audit
// label) and the count of samples awaiting a decision, sorted by analysis name. The QA worklist
// screen reads this; clicking an entry opens the modal replica for that analysis.
export const selectQaAnalysisWorklist = (state) => {
  const byAnalysis = new Map()
  state.limsResults.items.forEach((r) => {
    if (r.state !== STATE.SENT_TO_QA) return
    const key = r.analysisId || "—"
    if (!byAnalysis.has(key)) {
      byAnalysis.set(key, {
        analysisId: r.analysisId || null,
        analysisLabel: resultLabel(state, r),
        pending: 0,
      })
    }
    byAnalysis.get(key).pending += 1
  })
  return Array.from(byAnalysis.values()).sort((a, b) =>
    (a.analysisLabel || "").localeCompare(b.analysisLabel || "")
  )
}

// All results for ONE analysis that are in the QA stage — still awaiting a decision (sent-to-qa)
// OR already decided (qa-approved / qa-rerun / qa-stopped) — most recent first. The QA modal shows
// the whole analysis (every sample QA saw), not only the still-pending rows, so a re-open reflects
// decisions already committed.
export const selectQaResultsForAnalysis = (state, analysisId) =>
  state.limsResults.items.filter(
    (r) =>
      r.analysisId === analysisId &&
      (r.state === STATE.SENT_TO_QA ||
        r.state === STATE.QA_APPROVED ||
        r.state === STATE.QA_RERUN ||
        r.state === STATE.QA_STOPPED)
  )

// Whether a definition has a spec with usable QC content (≥1 parameter or a non-empty
// formula). Mirrors the F-B2 specHasContent helper.
function specHasContent(spec) {
  if (!spec) return false
  const hasParams = Array.isArray(spec.parameters) && spec.parameters.length > 0
  const hasFormula = typeof spec.formula === "string" && spec.formula.trim() !== ""
  return hasParams || hasFormula
}

// Build a name→definition lookup from the analysisDefinitions map. Planned analyses carry the
// definition NAME (not a stable id — the deployed analysis endpoints expose none), so the
// worklist resolves an analysis to its definition by name, exactly as F-C1/F-B2 do. SP-E
// should add analysis_definition_id to the analysis to make this an id join — see
// lims-api-contract.md.
function definitionsByName(state) {
  const byName = {}
  Object.values(state.analysisDefinitions || {}).forEach((def) => {
    if (def && def.name && byName[def.name] === undefined) {
      byName[def.name] = def
    }
  })
  return byName
}

// The Worklist (F-C2 surface b): QC analyses AWAITING a result — planned analyses whose
// resolved definition has a spec (F-B1 content) but no saved result yet. A read-only lens
// over the existing planned-analysis data; it does not duplicate the team-plan queue.
// Returns [{ analysisId, analysisName, analysisDefinitionId }] sorted by analysis name.
export const selectAnalysesAwaitingResult = (state) => {
  const byName = definitionsByName(state)
  const resultsByAnalysis = new Set(state.limsResults.items.map((r) => r.analysisId))
  const planned = (state.analyses && state.analyses.plannedAnalyses) || {}
  return Object.values(planned)
    .map((analysis) => {
      const def = analysis && analysis.name ? byName[analysis.name] : undefined
      if (!def) return null
      const spec = state.limsSpecs[def.id]
      if (!specHasContent(spec)) return null
      if (resultsByAnalysis.has(analysis.id)) return null
      return {
        analysisId: analysis.id,
        analysisName: analysis.name,
        analysisDefinitionId: def.id,
      }
    })
    .filter(Boolean)
    .sort((a, b) => (a.analysisName || "").localeCompare(b.analysisName || ""))
}

// Resolve a planned analysis to its analysis-definition id by NAME (the conservative join
// described above). Exposed so the result-entry panel can load the spec when wired into the
// analysis task, which only carries the analysis (name), not the definition id. Returns null
// when no definition matches the analysis's current name.
export const selectAnalysisDefinitionIdForAnalysis = (state, analysisId) => {
  const analysis = state.analyses && state.analyses.plannedAnalyses[analysisId]
  if (!analysis || !analysis.name) return null
  const def = definitionsByName(state)[analysis.name]
  return def ? def.id : null
}
